import {t, useSiteLanguage} from "../../i18n";
import {CSSProperties, ImgHTMLAttributes, useEffect, useRef, useState} from 'react';
import {ResponsiveImageAsset} from '../../assets/imageAssets';
import './progressiveImage.css';

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'width' | 'height'> & {
    asset: ResponsiveImageAsset;
    priority?: boolean;
    /** The lightbox provides its own frame and loading feedback. */
    unframed?: boolean;
};

type ImageState = 'loading' | 'ready' | 'error';
let visibilityObserver: IntersectionObserver | undefined;
const observedFrames = new Set<HTMLElement>();

function observeLoadingFrame(frame: HTMLElement) {
    if (!('IntersectionObserver' in window)) return () => {};
    visibilityObserver ??= new IntersectionObserver(entries => {
        entries.forEach(({target, isIntersecting}) => target.toggleAttribute('data-in-view', isIntersecting));
    });
    observedFrames.add(frame);
    visibilityObserver.observe(frame);
    return () => {
        visibilityObserver?.unobserve(frame);
        observedFrames.delete(frame);
        if (!observedFrames.size) {
            visibilityObserver?.disconnect();
            visibilityObserver = undefined;
        }
    };
}

/** Reserve the frame immediately and fade decoded pixels over a quiet loading state. */
export default function ProgressiveImage({asset, priority = false, unframed = false, className = '',
    style, loading, alt = '', onLoad, onError, ...props}: Props) {
    useSiteLanguage();
    const ref = useRef<HTMLImageElement>(null);
    const frameRef = useRef<HTMLSpanElement>(null);
    const [result, setResult] = useState<{source: string; state: ImageState}>({source: asset.src, state: 'loading'});
    const state = result.source === asset.src ? result.state : 'loading';

    useEffect(() => {
        const img = ref.current;
        let active = true;
        const reveal = async () => {
            if (!img?.complete) return;
            if (!img.naturalWidth) {
                if (active) setResult({source: asset.src, state: 'error'});
                return;
            }
            try { await img.decode(); } catch { /* Loaded pixels remain usable if decode rejects. */ }
            if (active) setResult({source: asset.src, state: 'ready'});
        };
        img?.addEventListener('load', reveal);
        img?.addEventListener('error', reveal);
        void reveal();
        return () => {
            active = false;
            img?.removeEventListener('load', reveal);
            img?.removeEventListener('error', reveal);
        };
    }, [asset.src]);

    useEffect(() => {
        if (state !== 'loading' || !frameRef.current) return;
        return observeLoadingFrame(frameRef.current);
    }, [state, unframed, asset.src]);

    const image = <img {...props} alt={alt} ref={ref} src={asset.src} srcSet={asset.srcSet}
        width={asset.width} height={asset.height} loading={loading ?? (priority ? 'eager' : 'lazy')}
        decoding="async" {...(priority ? {fetchpriority: 'high'} : {})}
        className={`progressive-image${unframed ? ` ${className}` : ''}`}
        style={unframed ? style : undefined} data-ready={state === 'ready' || undefined}
        onLoad={onLoad} onError={onError} />;

    if (unframed) return image;

    return <span ref={frameRef} className={`image-frame ${className}`} data-image-state={state}
        aria-busy={state === 'loading'} style={{aspectRatio: `${asset.width} / ${asset.height}`, ...style} as CSSProperties}>
        <span className="image-placeholder" aria-hidden="true">
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.2">
                <rect x="5" y="5" width="22" height="22" rx="4" />
                <circle cx="12" cy="12" r="2" />
                <path d="m6 23 7-7 4 4 4-5 6 8" />
            </svg>
            <span>{state === 'error' ? t("gallery.unavailable") : t("common.loading")}</span>
        </span>
        {image}
    </span>;
}
